import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Optional,
  Output,
  Self,
  SimpleChanges,
  ViewChild,
  ViewEncapsulation,
} from '@angular/core';
import {
  AbstractControl,
  ControlValueAccessor,
  FormGroupDirective,
  NgControl,
  UntypedFormControl,
  ValidationErrors,
  ValidatorFn,
  Validators,
} from '@angular/forms';
import { MatMenuTrigger } from '@angular/material/menu';
import { EmojiData, EmojiService } from '@ctrl/ngx-emoji-mart/ngx-emoji';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { flatten } from 'lodash';
import { tap } from 'rxjs/operators';

import { CustomControlAbstract } from '@app/components/abstract/custom-control.abstract';
import { UserData } from '@shared/interfaces/user-data';
import { RestUserModel } from '@shared/interfaces/user.interface';
import { MentionConfig } from '@ui-components/components/mentions/mention-config';

export const StringNotEmptyValidator = (): ValidatorFn => {
  return (control: AbstractControl): { [key: string]: boolean } => {
    const value = control?.value || '';
    if (value.replace(/<br>/gim, '').trim() !== '') {
      return null;
    }
    return { stringNotEmptyValidator: true };
  };
};

@UntilDestroy()
@Component({
  selector: 'app-content-editable-area',
  templateUrl: './content-editable-area.component.html',
  styleUrls: ['./content-editable-area.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.None,
})
export class ContentEditableAreaComponent
  extends CustomControlAbstract<any>
  implements OnInit, OnDestroy, OnChanges, ControlValueAccessor
{
  control = new UntypedFormControl('', [Validators.required, StringNotEmptyValidator()]);
  errors: ValidationErrors;
  invalid = false;
  disabled = false;
  emojiActive = false;
  storedSelectionRange: Range;

  @ViewChild(MatMenuTrigger) emmojiMenu: MatMenuTrigger;

  @Input() sendButton = false;
  @Input() autoresize = false;
  @Input() maxHeight: number;
  @Input() displayNewLineHint = false;
  @Input() attrPlaceholder = '';
  @Input() attrName: string;
  @Input() label: string;
  @Input() labelCss = 'nowrap body-small-bold';
  @Input() contentCss = 'display-flex align-items-start';
  @Input() containerCss = 'display-flex flex-column';
  @Input() inputCss = '';
  @Input() errorSection = true;
  @Input() markAsInvalid = false;
  @Input() labelRequired = false;
  @Input() displayCounter = false;
  @Input() maxCounter: number;
  @Output() keydownEnterEvent = new EventEmitter<void>();
  @Output() blurEvent = new EventEmitter<void>();
  @Input() userData: UserData;
  @Input() useEmoji = true;
  @Input() mentions: MentionConfig;

  @Input() set attrDisable(attrDisable: boolean) {
    if (attrDisable) {
      this.control.disable();
    } else {
      this.control.enable();
    }
    this.disabled = attrDisable;
    this.cdr.detectChanges();
  }

  @Input() set value(value: string) {
    this.control.setValue(this.convertValueToHtml(value));
  }

  readonly EMOJI_MAPPING = {
    ':)': ':slightly_smiling_face:',
    ';)': ':wink:',
    ':D': ':smile:',
    ':O': ':open_mouth:',
    ':(': ':worried:',
    ':P': ':stuck_out_tongue:',
    ':\\': ':confused:',
    ':|': ':neutral_face:',
    '8)': ':sunglasses:',
  };

  @ViewChild('editInput', { read: ElementRef, static: false }) editInput: ElementRef<HTMLElement>;

  defaultMentionConfig: MentionConfig = {
    items: [],
    labelKey: 'displayName',
    mentionSelect: () => {
      return '';
    },
    mentionFilter: (searchString: string, items: RestUserModel[]) => {
      const r = new RegExp(searchString?.toLowerCase(), 'i');
      return items.filter(
        x =>
          (searchString?.length === 2 &&
            x.firstName?.startsWith(searchString[0]) &&
            x.lastName?.startsWith(searchString[1])) ||
          r.test(x.displayName)
      );
    },
    triggerChar: '@',
    allowSpace: true,
    dropUp: true,
  };
  defaultEmojiMentionConfig: MentionConfig = {
    items: this.emojiService.emojis.map(e => ({ displayName: `${e.native} :${e.shortName}:`, ...e })),
    labelKey: 'displayName',
    mentionSelect: () => {
      return '';
    },
    mentionFilter: (searchString: string, items: EmojiData[]) => {
      if (searchString?.length >= 2) {
        const shortNameResults = items.filter(x => {
          const shortNames = [...x.shortName.split('_')];
          return shortNames.some(k => k.toLowerCase().startsWith(searchString?.toLowerCase()));
        });

        const keywordResults = items.filter(x => {
          const keywords = [...flatten(x.keywords.map(k => k.split('_')))];
          return keywords.some(k => k.toLowerCase().startsWith(searchString?.toLowerCase()));
        });
        return shortNameResults.concat(keywordResults);
      }
      return [];
    },
    maxItems: 100,
    triggerChar: ':',
    returnTrigger: true,
    allowSpace: false,
    dropUp: true,
  };
  mentionConfig: MentionConfig = { mentions: [] };

  // customEmojis = [
  //   {
  //     name: 'lyman-yes',
  //     shortNames: ['lyman-yes'],
  //     text: '',
  //     emoticons: [],
  //     keywords: ['lyman-yes'],
  //     imageUrl: 'https://emoji.slack-edge.com/T8W1D98L8/lyman-yes/244c8c4e0c00ae79.gif',
  //   },
  // ];

  constructor(
    @Self() @Optional() protected ngControl: NgControl,
    protected cdr: ChangeDetectorRef,
    private emojiService: EmojiService,
    @Optional() formDirective: FormGroupDirective
  ) {
    super(ngControl, cdr, formDirective);
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes.mentions || changes.useEmoji) {
      this.setMentions();
      this.cdr.detectChanges();
    }
  }

  setMentions() {
    const mentions = [];
    if (this.mentions) {
      mentions.push({ ...this.defaultMentionConfig, ...this.mentions });
    }
    if (this.useEmoji) {
      mentions.push({ ...this.defaultEmojiMentionConfig });
    }
    this.mentionConfig = { mentions: mentions };
  }

  ngOnInit(): void {
    this.setMentions();

    this.control.valueChanges
      .pipe(
        untilDestroyed(this),
        tap(value => {
          this.onChanged(this.convertValueToString(value));
          this.onTouched();
        })
      )
      .subscribe();

    this.control.statusChanges
      .pipe(
        untilDestroyed(this),
        tap(() => {
          this.checkControlStatus(this.ngControl.control.status);
        })
      )
      .subscribe();

    if (this.ngControl?.control) {
      if (this.ngControl.control.errors) {
        this.errors = { ...this.ngControl.control.errors };
      }
      this.checkStatus();
      this.ngControl.control.statusChanges
        .pipe(
          untilDestroyed(this),
          tap(status => {
            this.checkControlStatus(status);
          })
        )
        .subscribe();
    }
  }

  ngOnDestroy(): void {}

  writeValue(value: any): void {
    this.control.setValue(value);
  }

  onBlur() {
    this.blurEvent.emit();
  }

  enter($event) {
    if (this.keydownEnterEvent.observed) {
      this.send();
      $event.preventDefault();
    }
  }

  space($event) {
    this.validateInput();
  }

  send() {
    if (this.control.valid) {
      this.keydownEnterEvent.emit();
    }
  }

  insertBr() {
    const selection = window.getSelection();
    if (selection && selection.rangeCount > 0) {
      const range = selection.getRangeAt(0);
      const fragment = document.createDocumentFragment();
      fragment.append(document.createElement('br'));
      const text = document.createTextNode('\u200B');
      fragment.append(text);
      range.insertNode(fragment);
      range.collapse();
      this.cdr.detectChanges();
    }
  }

  mentionItemSelected($event: RestUserModel | EmojiData) {
    if (!$event) {
      return;
    }

    const selection = window.getSelection();
    if (selection && selection.rangeCount > 0) {
      const range = selection.getRangeAt(0);

      const user = $event as RestUserModel;
      if (user.email) {
        const fragment = document.createDocumentFragment();
        const text = document.createTextNode(' \u200B');
        const node = document.createElement('span');
        node.textContent = '@' + user.displayName;
        node.className = `user-mention ${this.userData?.id === user.id ? 'me' : ''}`;
        node.setAttribute('contentEditable', 'false');
        node.setAttribute('email', user.email);
        fragment.append(node);
        fragment.append(text);
        range.insertNode(fragment);
        setTimeout(() => {
          selection.removeAllRanges();
          const nodeRange = new Range();
          nodeRange.setStartAfter(text);
          nodeRange.setEndAfter(text);
          selection.addRange(range);
          selection.collapseToEnd();
          this.cdr.detectChanges();
        }, 0);
      }
      const emoji = $event as EmojiData;
      if (emoji.native) {
        const text = document.createTextNode(emoji.native);
        range.insertNode(text);
        setTimeout(() => {
          range.setStartAfter(text);
          range.setEndAfter(text);
          selection.removeAllRanges();
          selection.addRange(range);
          selection.collapseToEnd();
          this.control.setValue(this.editInput.nativeElement.innerHTML, { emitModelToViewChange: false });
          this.cdr.detectChanges();
        }, 0);
      }
    }
  }

  private convertValueToHtml(strContent: string): string {
    return strContent;
  }

  private convertValueToString(htmlContent: string): string {
    if (!htmlContent) {
      return '';
    }

    const div = document.createElement('div');
    div.innerHTML = htmlContent.trim();
    const mentions = div.querySelectorAll('.user-mention');
    mentions.forEach(m => {
      m.replaceWith(document.createTextNode(`[~${m.getAttribute('email')}]`));
    });
    const brs = div.querySelectorAll('br');
    brs.forEach(m => {
      m.replaceWith(document.createTextNode('\n'));
    });

    let content = div.textContent;

    Object.keys(this.EMOJI_MAPPING).forEach(key => {
      const regex = new RegExp(` ${key.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}`, 'gmi');
      const emoji = this.emojiService.emojis.find(x => x.colons === this.EMOJI_MAPPING[key]);
      if (emoji) {
        content = content.replace(regex, ` ${emoji.native}`);
        if (content === key) {
          content = emoji.native;
        }
      }
    });

    return content;
  }

  getValueLength() {
    return this.convertValueToString(this.control.value).length;
  }

  addEmoji($event: any) {
    const selection = window.getSelection();
    if (selection && this.storedSelectionRange) {
      selection.removeAllRanges();
      selection.addRange(this.storedSelectionRange);
      if (selection.rangeCount > 0) {
        const range = selection.getRangeAt(0);
        const text = document.createTextNode($event.emoji.native);
        range.insertNode(text);
        range.setStartAfter(text);
        range.setEndAfter(text);
        selection.removeAllRanges();
        selection.addRange(range);
        selection.collapseToEnd();
        this.control.setValue(this.editInput.nativeElement.innerHTML, { emitModelToViewChange: false });
      }
    } else {
      const value = this.control.value || '';
      this.control.setValue(value + $event.emoji.native);
    }
    this.emmojiMenu.closeMenu();
    this.cdr.detectChanges();
  }

  stopPropagation($event: MouseEvent) {
    $event.preventDefault();
    $event.stopPropagation();
  }

  storeSelection() {
    this.storedSelectionRange = null;
    const selection = window.getSelection();
    if (selection && selection.rangeCount > 0) {
      const range = selection.getRangeAt(0);
      if (this.editInput.nativeElement.contains(range.startContainer)) {
        this.storedSelectionRange = range;
      }
    }
  }

  validateInput() {
    setTimeout(() => {
      const selection = window.getSelection();
      if (selection && selection.rangeCount > 0) {
        const range = selection.getRangeAt(0);
        const emojiText = range.startContainer.textContent.substring(range.endOffset - 3, range.endOffset - 1);
        const mapValue = this.EMOJI_MAPPING[emojiText];
        if (mapValue) {
          const emoji = this.emojiService.emojis.find(x => x.colons === mapValue);
          if (emoji) {
            range.setStart(range.startContainer, range.endOffset - 3);
            const emojiNode = document.createTextNode(emoji.native + ' ');
            range.deleteContents();
            range.insertNode(emojiNode);
            range.setStart(emojiNode, 2);
            range.setEnd(emojiNode, 2);
            selection.removeAllRanges();
            selection.addRange(range);
            this.control.setValue(this.editInput.nativeElement.innerHTML, { emitModelToViewChange: false });
          }
        }
      }
    }, 0);
  }
}
